<script setup lang="ts">
  import { defineProps, defineEmits, computed } from 'vue'
  import {
    type CustomerDocumentTemplate,
    ImagePresetEnum,
    type ImageproxyImagePreset
  } from '@/types/generated'
  import Image from '@/components/util/Image.vue'

  const props = defineProps<{
    template: CustomerDocumentTemplate
    isSelected?: boolean
  }>()

  // Define Emits for the `select` event
  const emit = defineEmits<{
    (e: 'select', template: CustomerDocumentTemplate): void
  }>()

  // Function to handle clicks and emit the event
  function handleClick() {
    emit('select', props.template) // Emits the `select` event with the template data
  }

  const thumbnailImage = computed<ImageproxyImagePreset>(() => {
    return props.template.document.images?.find(
      (image) => image.image_preset === ImagePresetEnum.THUMBNAIL
    )
  })
</script>

<template>
  <div
    @click="handleClick"
    class="flex cursor-pointer items-start space-x-4 border-b border-gray-200 p-4"
    :class="{
      'bg-gray-100': isSelected, // Highlight selected
      'hover:bg-gray-50': !isSelected // Hover style for others
    }"
  >
    <!-- Thumbnail Image -->
    <Image
      :src="thumbnailImage?.url"
      alt="Template Thumbnail"
      :width="'80px'"
      :height="'120px'"
      class="h-30 w-20 rounded-xl border border-gray-200"
    />

    <!-- Template Info -->
    <div>
      <!-- Template Name and Badge -->
      <div class="flex items-center space-x-2">
        <span class="font-bold">{{ template.name }}</span>
        <span
          class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700
            ring-1 ring-inset ring-purple-700/10"
          v-if="template.is_jupus_template"
        >
          Jupus
        </span>
        <span
          class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1
            ring-inset ring-red-700/10"
          v-else
        >
          Kanzlei
        </span>
      </div>

      <!-- Template Description -->
      <p class="mt-1 text-xs text-gray-600">
        {{ template.description.slice(0, 260) }}
        <span v-if="template.description.length > 260">...</span>
      </p>
    </div>
  </div>
</template>
